/**
 * toolsAtoms.ts
 *
 * Atoms for managing tools state
 *
 * Primary responsibilities:
 * - Store available tools and platform tools
 * - Track selected tools and active tool
 * - Manage tool-specific settings and configurations
 */

import { atom } from 'jotai'
import { atomWithStorage, atomFamily } from 'jotai/utils'
import { Tables } from '@/supabase/types'

// Custom type for prompt variables
export type PromptVariable = {
  promptId: string
  name: string
  value: string
}

// Store user-created tools
export const toolsAtom = atom<Tables<'tools'>[]>([])

// Store platform-provided tools
export const platformToolsAtom = atom<Tables<'tools'>[]>([])

// Store currently selected tools for a chat
export const selectedToolsAtom = atomWithStorage<Tables<'tools'>[]>('selectedTools', [])

// Track which tool is currently in use
export const toolInUseAtom = atomWithStorage<string | 'none'>('toolInUse', 'none')

// Retrieval settings
export const useRetrievalAtom = atomWithStorage<boolean>('useRetrieval', false)

export const sourceCountAtom = atomWithStorage<number>('sourceCount', 4)

// Prompt variables for tools
export const promptVariablesAtom = atomWithStorage<PromptVariable[]>('promptVariables', [])

// UI state for prompt variables
export const showPromptVariablesAtom = atomWithStorage<boolean>('showPromptVariables', false)

// Derived atom for available tools (combination of user and platform tools)
export const availableToolsAtom = atom<Tables<'tools'>[]>((get) => {
  const userTools = get(toolsAtom)
  const platformTools = get(platformToolsAtom)

  // Combine and deduplicate tools
  return [...userTools, ...platformTools].filter(
    (tool, index, self) => index === self.findIndex((t) => t.id === tool.id)
  )
})

// Active tool data
export const activeToolAtom = atom<Tables<'tools'> | null>((get) => {
  const toolId = get(toolInUseAtom)
  if (toolId === 'none') return null

  const allTools = get(availableToolsAtom)
  return allTools.find((tool) => tool.id === toolId) || null
})

// ===== Tool execution state =====
export interface ToolExecutionState {
  status: 'idle' | 'running' | 'completed' | 'failed'
  result: any | null
  error: string | null
  startTime: number | null
  endTime: number | null
  progress?: number
}

const defaultToolExecutionState: ToolExecutionState = {
  status: 'idle',
  result: null,
  error: null,
  startTime: null,
  endTime: null
}

// Track execution state for each tool
export const toolExecutionStateAtom = atomFamily((toolId: string) =>
  atom<ToolExecutionState>(defaultToolExecutionState)
)

// Current execution state for debugging
export const currentToolExecutionAtom = atom<string | null>(null)

// ===== Tool history atoms =====
export interface ToolExecutionHistoryItem {
  id: string
  toolId: string
  toolName?: string // Make toolName optional
  timestamp: number
  duration: number
  status: 'success' | 'error' | 'pending'
  params: Record<string, any>
  result?: any
  error?: string
}

// Last 50 tool executions
export const toolExecutionHistoryAtom = atomWithStorage<ToolExecutionHistoryItem[]>(
  'toolExecutionHistory',
  []
)

// Maximum number of history items to keep
export const maxToolHistoryItemsAtom = atom(50)

// ===== Tool settings atoms =====
export interface ToolSettings {
  autoExecuteTools: boolean
  confirmDangerousOperations: boolean
  showToolExecutionDetails: boolean
}

export const toolSettingsAtom = atomWithStorage<ToolSettings>('toolSettings', {
  autoExecuteTools: true,
  confirmDangerousOperations: true,
  showToolExecutionDetails: false
})

// ===== Helper functions =====

// Define types for getters
type ToolExecutionAtomGetters = {
  toolExecutionStateAtom: ReturnType<
    typeof atomFamily<string, ReturnType<typeof atom<ToolExecutionState>>>
  >
  currentToolExecutionAtom: ReturnType<typeof atom<string | null>>
}

// Add MAX_HISTORY_ITEMS constant
const MAX_HISTORY_ITEMS = 50

// Fix the implementation to properly use tools parameter
export const startToolExecution = (execution: {
  toolId: string
  params: Record<string, any>
  startTime: number
}) => {
  return (get: any, set: any) => {
    // Set tool execution state
    set(toolExecutionStateAtom(execution.toolId), {
      status: 'running',
      result: null,
      error: null,
      startTime: execution.startTime,
      endTime: null
    })

    // Set current tool execution
    set(currentToolExecutionAtom, execution.toolId)

    // Add to history
    set(toolExecutionHistoryAtom, (prev: ToolExecutionHistoryItem[]) => {
      const newItem: ToolExecutionHistoryItem = {
        id: `${execution.toolId}-${Date.now()}`,
        toolId: execution.toolId,
        toolName: 'Tool', // This is now allowed by the interface
        timestamp: Date.now(),
        duration: 0,
        status: 'pending',
        params: execution.params || {}
      }

      return [newItem, ...prev].slice(0, MAX_HISTORY_ITEMS)
    })
  }
}

// Complete a tool execution
export const completeToolExecution = (result: any) => {
  return (get: any, set: any) => {
    // Get current tool execution
    const currentToolId = get(currentToolExecutionAtom)
    if (!currentToolId) return

    // Update tool execution state
    set(toolExecutionStateAtom(currentToolId), (prev: ToolExecutionState) => ({
      ...prev,
      status: 'completed',
      result,
      endTime: Date.now()
    }))

    // Clear current tool execution
    set(currentToolExecutionAtom, null)

    // Update history
    set(toolExecutionHistoryAtom, (prev: ToolExecutionHistoryItem[]) => {
      // Find running item for this tool
      const existingItemIndex = prev.findIndex(
        (item) => item.toolId === currentToolId && item.status === 'pending'
      )

      if (existingItemIndex >= 0) {
        const newItems = [...prev]
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          status: 'success',
          result,
          duration: Date.now() - newItems[existingItemIndex].timestamp
        }
        return newItems
      }

      // If no running item found, add a new completed one
      const newItem: ToolExecutionHistoryItem = {
        id: `${currentToolId}-${Date.now()}`,
        toolId: currentToolId,
        toolName: 'Tool', // This is now allowed by the interface
        timestamp: Date.now(),
        duration: 0,
        status: 'success',
        params: {},
        result
      }

      return [newItem, ...prev].slice(0, MAX_HISTORY_ITEMS)
    })
  }
}

// Add tool execution failure function
export const failToolExecution = (error: string) => {
  return (get: any, set: any) => {
    // Get current tool execution
    const currentToolId = get(currentToolExecutionAtom)
    if (!currentToolId) return

    // Update tool execution state
    set(toolExecutionStateAtom(currentToolId), (prev: ToolExecutionState) => ({
      ...prev,
      status: 'failed',
      error,
      endTime: Date.now()
    }))

    // Clear current tool execution
    set(currentToolExecutionAtom, null)

    // Update history
    set(toolExecutionHistoryAtom, (prev: ToolExecutionHistoryItem[]) => {
      // Find running item for this tool
      const existingItemIndex = prev.findIndex(
        (item) => item.toolId === currentToolId && item.status === 'pending'
      )

      if (existingItemIndex >= 0) {
        const newItems = [...prev]
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          status: 'error',
          error,
          duration: Date.now() - newItems[existingItemIndex].timestamp
        }
        return newItems
      }

      // If no running item found, add a new failed one
      const newItem: ToolExecutionHistoryItem = {
        id: `${currentToolId}-${Date.now()}`,
        toolId: currentToolId,
        toolName: 'Tool', // This is now allowed by the interface
        timestamp: Date.now(),
        duration: 0,
        status: 'error',
        params: {},
        error
      }

      return [newItem, ...prev].slice(0, MAX_HISTORY_ITEMS)
    })
  }
}

// Add tool progress update function
export const updateToolProgress = (toolId: string, progress: number) => {
  return (get: any, set: any) => {
    set(toolExecutionStateAtom(toolId), (prev: ToolExecutionState) => ({
      ...prev,
      progress
    }))
  }
}

// Add/remove tools from the selected tools list
export const addToolToSelection = (tool: Tables<'tools'>) => {
  return (get: any, set: any) => {
    set(selectedToolsAtom, (prev: Tables<'tools'>[]) => {
      // Check if tool is already selected
      if (prev.some((t) => t.id === tool.id)) {
        return prev
      }

      // Add tool to selection
      return [...prev, tool]
    })
  }
}

export const removeToolFromSelection = (toolId: string) => {
  return (get: any, set: any) => {
    set(selectedToolsAtom, (prev: Tables<'tools'>[]) => prev.filter((tool) => tool.id !== toolId))
  }
}

export const clearToolSelection = () => {
  return (get: any, set: any) => {
    set(selectedToolsAtom, [])
  }
}

// Alias functions for external use
export const selectTool = addToolToSelection
export const deselectTool = removeToolFromSelection
export const clearSelectedTools = clearToolSelection
